<template>
	<v-card
		class="editor-card sgnl-flex sgnl-flex-column sgnl-justify-space-between"
		height="100%"
	>
		<!-- Header bar -->
		<div class="editor-header">
			<!-- Left-side buttons -->
			<div class="editor-header__group">
				<!-- Language selector -->
				<v-menu v-if="selectedCodeLanguage">
					<template v-slot:activator="{ props: menuProps }">
						<v-tooltip>
							<template v-slot:activator="{ props: tooltipProps }">
								<v-btn
									variant="outlined"
									size="small"
									class="btn-secondary-small"
									:class="{ 'btn-secondary-small--active': selectedCodeLanguage }"
									v-bind="{ ...tooltipProps, ...menuProps }"
								>
									<Code16 />
									{{ selectedCodeLanguage.name }}
									<ChevronDown16 />
								</v-btn>
							</template>
							<span>Code Language</span>
						</v-tooltip>
					</template>
					<v-list>
						<v-list-item
							v-for="codeLanguage in codeLanguagesList"
							:key="codeLanguage.id"
							:disabled="!codeLanguage.active"
							link
							density="compact"
							@click="setCodeLanguage(codeLanguage)"
						>
							<v-list-item-title class="sgnl-m-0 sgnl-p-0 sgnl-paragraph-s-regular">{{
								codeLanguage.name
							}}</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>

				<!-- Core selector -->
				<v-tooltip>
					<template v-slot:activator="{ props: tooltipProps }">
						<v-btn
							variant="outlined"
							size="small"
							class="btn-secondary-small userflow-core-selector-editor"
							:class="{ 'btn-secondary-small--active': editorCore }"
							:loading="!coreList || !editorCore || editorStore.coreUpdateStatus.loading"
							v-bind="tooltipProps"
							data-test="core-selector-chip"
							@click="openSelectCoreDialog"
						>
							<Chip16 />
							{{ editorCore?.Name }}
							<SubtractFilled16
								v-if="editorCore?.Deprecated"
								class="sgnl-icon-color-error"
							/>
							<ChevronDown16 />
						</v-btn>
					</template>
					<span>Core</span>
				</v-tooltip>

				<!-- Run Arguments -->
				<v-tooltip>
					<template v-slot:activator="{ props: tooltipProps }">
						<v-btn
							variant="outlined"
							size="small"
							class="btn-secondary-small"
							:class="{ 'btn-secondary-small--active': !!runArguments }"
							:loading="editorStore.runArgumentsUpdateStatus.loading"
							v-bind="tooltipProps"
							data-test="runArgs-selector-chip"
							@click="openRunArgumentsDialog"
						>
							<Terminal16 />
							<ChevronDown16 />
						</v-btn>
					</template>
					<span>Command-Line Arguments</span>
				</v-tooltip>

				<!-- Compilation Flags -->
				<v-tooltip>
					<template v-slot:activator="{ props: tooltipProps }">
						<v-btn
							variant="outlined"
							size="small"
							class="btn-secondary-small"
							:class="{ 'btn-secondary-small--active': !!compilationFlags }"
							:loading="editorStore.compilationFlagsUpdateStatus.loading"
							v-bind="tooltipProps"
							data-test="compilationFlags-selector-chip"
							@click="openCompilationFlagsDialog"
						>
							<Tools16 />
							<ChevronDown16 />
						</v-btn>
					</template>
					<span>Compilation and link flags</span>
				</v-tooltip>

				<!-- Data Source -->
				<v-tooltip>
					<template v-slot:activator="{ props: tooltipProps }">
						<v-btn
							variant="outlined"
							size="small"
							class="btn-secondary-small"
							:class="{ 'btn-secondary-small--active': isLocalMountConfig(mountConfig) }"
							:loading="editorStore.dataSourceUpdateStatus.loading"
							v-bind="tooltipProps"
							data-test="data-source-selector-chip"
							@click="openMountDataSourcesDialog"
						>
							<DataCheck16 v-if="isLocalMountConfig(mountConfig)" />
							<DataError16 v-else />
							<ChevronDown16 />
						</v-btn>
					</template>
					<span>Mount Data Source</span>
				</v-tooltip>
			</div>

			<!-- Right-side toolbar buttons -->
			<div class="editor-header__group">
				<TooltipButton
					v-if="!disableVariableViewer"
					:icon="true"
					@click="toggleVariableViewerVisibility"
					tooltipText="Toggle variable viewer"
					data-test="editor-toggle-variable-viewer-button"
				>
					<template #btnContent>
						<ValueVariable16
							:class="variableViewerVisibility ? 'sgnl-icon-color-brand' : 'sgnl-icon-color-secondary'"
						/>
					</template>
				</TooltipButton>
				<TooltipButton
					v-if="!disableLayoutChange && !$vuetify.display.mobile"
					:icon="true"
					@click="toggleEditorLayout"
					:disabled="!layoutChangeButtonEnabled"
					tooltipText="Toggle Layout"
				>
					<template #btnContent>
						<ReflectVertical16 v-if="editorLayout == 'horizontal'" />
						<ReflectHorizontal16 v-else />
					</template>
				</TooltipButton>
				<TooltipButton
					v-if="!$vuetify.display.mobile"
					:icon="true"
					@click="toggleEditorPreferredTheme"
					tooltipText="Set editor to preferred theme"
				>
					<template #btnContent>
						<Star16 />
					</template>
				</TooltipButton>
				<TooltipButton
					v-if="!$vuetify.display.mobile"
					:icon="true"
					@click="toggleEditorWhiteTheme"
					tooltipText="Set editor to light theme"
				>
					<template #btnContent>
						<Sun16 />
					</template>
				</TooltipButton>
				<TooltipButton
					v-if="!$vuetify.display.mobile"
					:icon="true"
					@click="toggleEditorDarkTheme"
					tooltipText="Set editor to dark theme"
				>
					<template #btnContent>
						<Moon16 />
					</template>
				</TooltipButton>
				<TooltipButton
					v-if="!$vuetify.display.mobile"
					:icon="true"
					@click="resetCodeToDefault"
					tooltipText="Restore editor code to default example"
				>
					<template #btnContent>
						<Restart16 />
					</template>
				</TooltipButton>
				<TooltipButton
					v-if="!$vuetify.display.mobile"
					:icon="true"
					@click="toggleFullscreen"
					tooltipText="Toggle fullscreen"
				>
					<template #btnContent>
						<Minimize16 v-if="isFullscreen" />
						<Maximize16 v-else />
					</template>
				</TooltipButton>
				<TooltipButton
					v-if="foldable && !$vuetify.display.mobile"
					:icon="true"
					@click="showEditor = !showEditor"
					:tooltipText="showEditor ? 'Hide editor' : 'Show editor'"
				>
					<template #btnContent>
						<component :is="toggleIcon" />
					</template>
				</TooltipButton>
			</div>
		</div>

		<!-- Code editor area -->
		<div class="sgnl-flex-grow-1 sgnl-flex editor-code-area">
			<v-expand-transition>
				<vue-monaco-editor
					v-if="showEditor"
					ref="monacoEditor"
					:style="{ height: editorHeight, width: editorWidth }"
					v-model:value="codeBuffer"
					:language="monacoLanguageID"
					:theme="editorTheme"
					:options="monacoEditorOptions"
					@change="saveChange"
					@mount="handleEditorMount"
				/>
			</v-expand-transition>
		</div>

		<!-- Footer control bar -->
		<EditorControlBar
			:taskStatus="taskStatus"
			:taskAccessState="taskAccessState"
			:taskID="taskID"
			:activeTask="activeTask"
			:buildID="buildID"
			:buildStatus="buildStatus"
			:buildAccessState="buildAccessState"
			:activeBuild="activeBuild"
			@create-task-request="$emit('create-task-request')"
			@cancel-active-task-request="$emit('cancel-active-task-request')"
			@cancel-active-build-request="$emit('cancel-active-build-request')"
			@retry-task-output="$emit('retry-task-output')"
			@retry-build-output="$emit('retry-build-output')"
		/>
		<RunArgumentsDialog
			v-if="showRunArgumentsDialog"
			:showDialog="showRunArgumentsDialog"
			:initialRunArguments="runArguments"
			@close-dialog="closeRunArgumentsDialog"
			@set-arguments="setRunArguments"
		/>
		<CompilationFlagsDialog
			v-if="showCompilationFlagsDialog"
			:showDialog="showCompilationFlagsDialog"
			:initialCompilationFlags="compilationFlags"
			@close-dialog="closeCompilationFlagsDialog"
			@set-flags="setCompilationFlags"
		/>
		<SelectCoreDialog
			v-if="showSelectCoreDialog"
			:showDialog="showSelectCoreDialog"
			:minimumCore="undefined"
			:activeCore="editorCore"
			@close-dialog="closeSelectCoreDialog"
			@set-core="setActiveCoreFromDialog"
		/>
		<MountDataSourcesDialog
			v-if="showMountDataSourcesDialog"
			:showDialog="showMountDataSourcesDialog"
			:initialMountConfig="mountConfig ?? undefined"
			@close-dialog="closeMountDataSourcesDialog"
			@set-data-sources="setMountConfig"
			@reset-data-sources="setMountConfig"
			@remove-data-sources="setMountConfig"
		/>
	</v-card>
</template>

<script lang="ts">
/* eslint-disable vue/multi-word-component-names */
import { defineComponent, markRaw, PropType } from "vue";

// Types
import { isLocalMountConfig, LocalMountConfig } from "@/types/api/dataSources";

// Libraries

// Utilities
import * as dsUtil from "@/components/DataSources/utilities";
import * as util from "@/js/utilities";

// Components
import EditorControlBar from "@/components/DevelopmentPlatform/EditorControlBar.vue";
import { VueMonacoEditor } from "@guolao/vue-monaco-editor";
import type { editor, IDisposable } from "monaco-editor";
import { EditorTheme, themes as themeList } from "@/js/editor";
import uxhwDefFileRaw from "@/assets/uxhw_definitions.json";

const editorThemes = import.meta.glob<unknown>("@/assets/editor-themes/*.json", {
	import: "default",
});
export const kDefaultTheme: EditorTheme = "solarized-light";
export const kDefaultDarkTheme: EditorTheme = "solarized-dark";
import RunArgumentsDialog from "@/components/RunArgumentsDialog.vue";
import CompilationFlagsDialog from "@/components/CompilationFlagsDialog.vue";
import SelectCoreDialog from "@/components/SelectCoreDialog.vue";
import MountDataSourcesDialog from "@/components/MountDataSourcesDialog.vue";
import TooltipButton from "@/components/Common/TooltipButton.vue";
import Code16 from "@carbon/icons-vue/es/code/16";
import Terminal16 from "@carbon/icons-vue/es/terminal/16";
import Tools16 from "@carbon/icons-vue/es/tools/16";
import ValueVariable16 from "@carbon/icons-vue/es/value--variable/16";
import Chip16 from "@carbon/icons-vue/es/chip/16";
import ReflectVertical16 from "@carbon/icons-vue/es/reflect--vertical/16";
import ReflectHorizontal16 from "@carbon/icons-vue/es/reflect--horizontal/16";
import Star16 from "@carbon/icons-vue/es/star/16";
import Sun16 from "@carbon/icons-vue/es/sun/16";
import Moon16 from "@carbon/icons-vue/es/moon/16";
import screenfull from "screenfull";
import DataCheck16 from "@carbon/icons-vue/es/data--check/16";
import DataError16 from "@carbon/icons-vue/es/data--error/16";
import ChevronDown16 from "@carbon/icons-vue/es/chevron--down/16";
import ChevronUp16 from "@carbon/icons-vue/es/chevron--up/16";
import Restart16 from "@carbon/icons-vue/es/restart/16";
import Maximize16 from "@carbon/icons-vue/es/maximize/16";
import Minimize16 from "@carbon/icons-vue/es/minimize/16";
import SubtractFilled16 from "@carbon/icons-vue/es/subtract--filled/16";

// Stores
import { mapState, mapStores } from "pinia";
import { useCoresStore } from "@/stores/cores";
import { useEditorStore } from "@/stores/editor";
import { useRootStore } from "@/stores/root";
import { SourceCodeLanguageObject } from "@/types/api/sourceCode";
import { Task, TaskStatus } from "@/types/api/tasks";
import { RemoteAccessState } from "@/types/general";
import { Core } from "@/types/api/cores";
import { Build, BuildStatus } from "@/types/api/builds";

// Global variables

// Local Types
type UxHwDefinition = {
	returnType: string;
	paramList: string[];
	prototype: string;
	functionName: string;
	documentation: string;
	documentationUrl?: string;
};

type UxHwCompletion = {
	label: string;
	functionPrototype: string;
	kind: number;
	insertText: string;
	insertTextRules: number;
	documentation: string;
};

const uxhwDefFile = uxhwDefFileRaw as UxHwDefinition[];

// uxhw.h is the same API from C and C++, and monaco keeps the two language IDs apart.
const kUxHwLanguageIDs = ["c", "cpp"];

// Each parameter becomes a numbered snippet placeholder so tab cycles through them.
function loadUxHwDefs(monaco: any): UxHwCompletion[] {
	return uxhwDefFile.map((definition) => {
		const parsedParamString = definition.paramList
			.map((value, index) => "${" + (index + 1) + ":" + value + "}")
			.join(",");
		return {
			label: definition.functionName,
			functionPrototype: definition.prototype,
			kind: monaco.languages.CompletionItemKind.Function,
			insertText: `${definition.functionName}(${parsedParamString})`,
			insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
			documentation:
				definition.documentation +
				(definition.documentationUrl ? `\n\n Read more: ${definition.documentationUrl}` : ""),
		};
	});
}

type ComponentData = {
	codeBuffer: string;
	monaco: boolean;
	monacoInstance: any;
	uxhwProviders: IDisposable[];
	loadedThemes: EditorTheme[];
	selectedCodeLanguage: undefined | SourceCodeLanguageObject;
	showRunArgumentsDialog: boolean;
	showCompilationFlagsDialog: boolean;
	showSelectCoreDialog: boolean;
	showMountDataSourcesDialog: boolean;
	showEditor: boolean;
	isFullscreen: boolean;
};

export default defineComponent({
	name: "Editor",
	components: {
		VueMonacoEditor,
		RunArgumentsDialog,
		CompilationFlagsDialog,
		SelectCoreDialog,
		MountDataSourcesDialog,
		EditorControlBar,
		TooltipButton,
		Code16,
		Chip16,
		ReflectVertical16,
		ReflectHorizontal16,
		Star16,
		Sun16,
		Moon16,
		DataCheck16,
		DataError16,
		Terminal16,
		Tools16,
		ValueVariable16,
		ChevronDown16,
		ChevronUp16,
		Restart16,
		Maximize16,
		Minimize16,
		SubtractFilled16,
	},
	emits: [
		"code-changed",
		"create-task-request",
		"cancel-active-task-request",
		"cancel-active-build-request",
		"retry-task-output",
		"retry-build-output",
	],
	props: {
		taskID: { type: String },
		taskStatus: { type: String as PropType<TaskStatus> },
		buildID: { type: String },
		buildStatus: { type: String as PropType<BuildStatus> },
		editorLayout: { type: String, default: "vertical" },
		editorHeight: { type: String, default: "60%" },
		editorWidth: { type: String, default: "100%" },
		variableViewerVisibility: { type: Boolean, default: false },
		disableVariableViewer: { type: Boolean, default: false },
		disableLayoutChange: { type: Boolean, default: false },
		foldable: { type: Boolean, default: false },
		activeTask: { type: Object as PropType<undefined | Partial<Task>> },
		activeBuild: { type: Object as PropType<undefined | Partial<Build>> },
		taskAccessState: { type: Object as PropType<RemoteAccessState> },
		buildAccessState: { type: Object as PropType<RemoteAccessState> },
	},
	data: (): ComponentData => ({
		codeBuffer: "",
		monaco: true,
		monacoInstance: null,
		uxhwProviders: [],
		loadedThemes: [],
		selectedCodeLanguage: undefined,
		showRunArgumentsDialog: false,
		showCompilationFlagsDialog: false,
		showSelectCoreDialog: false,
		showMountDataSourcesDialog: false,
		showEditor: true,
		isFullscreen: false,
	}),
	setup() {
		return { isLocalMountConfig };
	},
	watch: {
		editorTheme(newTheme: EditorTheme) {
			if (this.monacoInstance && newTheme) {
				this.loadAndSetTheme(newTheme);
			}
		},
	},
	methods: {
		handleEditorMount(editor: any, monacoRef: any) {
			this.monacoInstance = monacoRef;
			// @ts-ignore: Expose editor instance for onboarding tour scripts
			window.codeEditor = editor;
			this.loadAndSetTheme(kDefaultTheme);
			this.loadAndSetTheme(kDefaultDarkTheme);
			if (this.editorTheme) {
				this.loadAndSetTheme(this.editorTheme as EditorTheme);
			}
			this.registerUxHwProviders(monacoRef);
		},
		registerUxHwProviders(monacoRef: any) {
			// The editor remounts when hidden and shown again, so drop the old providers.
			this.disposeUxHwProviders();

			const uxhwCompletion = loadUxHwDefs(monacoRef);

			const completionProvider = {
				provideCompletionItems: (model: any, position: any) => {
					const word = model.getWordUntilPosition(position);
					const range = {
						startLineNumber: position.lineNumber,
						endLineNumber: position.lineNumber,
						startColumn: word.startColumn,
						endColumn: word.endColumn,
					};
					return { suggestions: uxhwCompletion.map((item) => ({ ...item, range })) };
				},
			};

			const hoverProvider = {
				provideHover: (model: any, position: any) => {
					const word = model.getWordAtPosition(position)?.word;
					if (!word) {
						return null;
					}
					const match = uxhwCompletion.find((item) => item.label === word);
					if (!match) {
						return null;
					}
					return {
						contents: [{ value: `**${match.functionPrototype}**` }, { value: match.documentation }],
					};
				},
			};

			this.uxhwProviders = kUxHwLanguageIDs.flatMap((languageID) => [
				monacoRef.languages.registerCompletionItemProvider(languageID, completionProvider),
				monacoRef.languages.registerHoverProvider(languageID, hoverProvider),
			]);
		},
		disposeUxHwProviders() {
			this.uxhwProviders.forEach((provider) => provider.dispose());
			this.uxhwProviders = [];
		},
		async loadTheme(key: EditorTheme) {
			if (!(key in themeList)) {
				return;
			}
			if (this.loadedThemes.includes(key)) {
				return;
			}
			const filename = themeList[key];
			const themeLoader = editorThemes[`/src/assets/editor-themes/${filename}.json`];
			const theme = await themeLoader();
			this.monacoInstance.editor.defineTheme(
				key,
				theme as Parameters<typeof this.monacoInstance.editor.defineTheme>[1],
			);
			this.loadedThemes.push(key);
		},
		async loadAndSetTheme(themeKey: EditorTheme) {
			await this.loadTheme(themeKey);
			this.monacoInstance.editor.setTheme(themeKey);
		},
		getCode() {
			// This function is used by components that use this component
			return this.codeBuffer;
		},
		getSelectedLanguage() {
			return this.selectedCodeLanguage;
		},
		getActiveCore() {
			// This function is used by components that use this component
			return this.editorCore;
		},
		setActiveCoreFromDialog(CoreID) {
			// Used for selecting active core from cores dialog
			this.closeSelectCoreDialog();
			this.editorStore.setEditorCore(CoreID, true);
			this.$posthog?.capture("editor_core_changed", { core_id: CoreID });
		},
		setActiveCore(chosenCore: Core | undefined) {
			if (chosenCore?.CoreID) {
				this.editorStore.setEditorCore(chosenCore.CoreID, true);
			} else {
				console.warn("Cannot set active core: core is undefined");
			}
		},
		switchToReferenceCore() {
			this.setActiveCore(this.coresStore.defaultReferenceCore);
		},
		setCodeLanguage(codeLanguage: SourceCodeLanguageObject) {
			const previousLanguage = this.selectedCodeLanguage?.name;
			this.selectedCodeLanguage = codeLanguage;
			this.$posthog?.capture("editor_language_changed", {
				language: codeLanguage?.name,
				previous_language: previousLanguage,
			});
			// localStorage.setItem("editor-language", JSON.stringify(this.selectedCodeLanguage));
		},
		saveChange() {
			// This saves the code to local storage on every update
			this.$emit("code-changed");
		},
		checkForCodeChangesAndSync(lastCode = "") {
			// TODO: might be worth doing this at the store level but using all of local storage
			if (lastCode !== this.codeBuffer) {
				this.editorStore.setEditorSourceCode(this.codeBuffer, true);
			}
			const currentCode = this.codeBuffer;
			setTimeout(() => {
				this.checkForCodeChangesAndSync(currentCode);
			}, this.editorStore.codeSyncFrequency);
		},

		toggleFullscreen() {
			if (screenfull.isEnabled) {
				screenfull.toggle(this.$el).catch((e) => {
					console.log(e);
				});
			}
		},
		handleFullscreenChange() {
			this.isFullscreen = screenfull.isFullscreen;
		},
		async resetCodeToDefault() {
			await this.editorStore.resetEditorSourceCode();
			this.codeBuffer = this.editorCode as string;
			this.checkForCodeChangesAndSync(this.editorCode);
		},
		toggleEditorWhiteTheme() {
			this.editorStore.setEditorTheme(kDefaultTheme, false);
		},
		toggleEditorDarkTheme() {
			this.editorStore.setEditorTheme(kDefaultDarkTheme, false);
		},
		toggleEditorPreferredTheme() {
			this.editorStore.setToPreferredTheme();
		},
		toggleEditorLayout() {
			this.editorStore.toggleEditorLayout(true);
		},
		toggleVariableViewerVisibility() {
			this.editorStore.toggleVariableViewerVisibility(true);
		},

		//* run arguments
		getRunArguments() {
			return this.runArguments;
		},
		getCompilationFlags() {
			return this.compilationFlags;
		},
		setRunArguments(data: { runArguments: string }) {
			this.editorStore.setEditorArgs(data.runArguments, true);
			this.closeRunArgumentsDialog();
		},
		openRunArgumentsDialog() {
			this.showRunArgumentsDialog = true;
		},
		closeRunArgumentsDialog() {
			this.showRunArgumentsDialog = false;
		},

		//* compilation flags
		setCompilationFlags(data: { compilationFlags: string }) {
			this.editorStore.setEditorCompilationFlags(data.compilationFlags, true);
			this.closeCompilationFlagsDialog();
		},
		openCompilationFlagsDialog() {
			this.showCompilationFlagsDialog = true;
		},
		closeCompilationFlagsDialog() {
			this.showCompilationFlagsDialog = false;
		},

		openSelectCoreDialog() {
			this.showSelectCoreDialog = true;
		},
		closeSelectCoreDialog() {
			this.showSelectCoreDialog = false;
		},

		// * data sources
		async getDataSources() {
			return await dsUtil.formatDataSourcesPayload(
				this.mountConfig?.ResourceID,
				this.mountConfig?.ResourceType,
				this.mountConfig?.Location,
			);
		},
		setMountConfig(mountConfig: LocalMountConfig | null | undefined) {
			this.editorStore.setEditorMountConfig(mountConfig, true);
			this.closeMountDataSourcesDialog();
		},
		openMountDataSourcesDialog() {
			this.showMountDataSourcesDialog = true;
		},
		closeMountDataSourcesDialog() {
			this.showMountDataSourcesDialog = false;
		},
	},
	computed: {
		...mapStores(useCoresStore, useEditorStore),
		...mapState(useRootStore, {
			codeLanguagesList: "codeLanguagesList",
		}),
		...mapState(useEditorStore, {
			editorCore: "editorCoreObject",
			mountConfig: "mountConfig",
			editorCode: "editorSourceCode",
			editorTheme: "editorThemeID",
			runArguments: "editorRunArgs",
			compilationFlags: "editorCompilationFlags",
		}),
		...mapState(useCoresStore, {
			coreList: "filteredCoresList",
		}),
		toggleIcon() {
			return markRaw(this.showEditor ? ChevronUp16 : ChevronDown16);
		},
		monacoLanguageID(): string {
			return this.selectedCodeLanguage?.monacoID ?? "c";
		},
		layoutChangeButtonEnabled() {
			if (this.$vuetify.display.xs) {
				return false;
			} else if (this.$vuetify.display.sm) {
				return false;
			} else if (this.variableViewerVisibility && (this.$vuetify.display.md || this.$vuetify.display.lg)) {
				return false;
			} else {
				return true;
			}
		},
		monacoEditorOptions(): editor.IStandaloneEditorConstructionOptions {
			return {
				fontFamily: util.getDesignToken("--sgnl-font-family-mono"),
				fontSize: 13.6,
				mouseWheelZoom: true,
				tabCompletion: "on",
				minimap: {
					enabled: !this.$vuetify.display.mobile,
				},
				selectOnLineNumbers: true,
				padding: { top: 5, bottom: 200 },
				scrollBeyondLastLine: false,
				scrollbar: { alwaysConsumeMouseWheel: false },
				automaticLayout: true,
			};
		},
	},
	created() {
		// @ts-ignore FIXME: Currently not working
		// this.codeLanguage = JSON.parse(localStorage.getItem("editor-language")) || this.codeLanguagesList[0];
		this.coresStore.getCoresFilteredCoresByUserTier();
	},
	async mounted() {
		util.waitFor(() => this.editorCode !== undefined).then(() => {
			this.codeBuffer = this.editorCode as string;
			this.checkForCodeChangesAndSync(this.editorCode);
		});
		await this.editorStore.syncPreferredTheme();
		if (screenfull.isEnabled) {
			screenfull.on("change", this.handleFullscreenChange);
		}

		// Initialize language to first active language in list
		util.waitFor(() => this.codeLanguagesList.length > 0).then(() => {
			if (!this.selectedCodeLanguage) {
				const langsList = this.codeLanguagesList as SourceCodeLanguageObject[];
				const defaultLang = langsList.find((lang) => lang.active) || langsList[0];
				this.selectedCodeLanguage = defaultLang;
			}
		});
	},
	beforeUnmount() {
		this.disposeUxHwProviders();
		if (screenfull.isEnabled) {
			screenfull.off("change", this.handleFullscreenChange);
		}
	},
});
</script>

<style>
.monaco-editor-container {
	text-align: left;
	resize: vertical;
	overflow: hidden;
}
</style>

<style scoped>
.editor-card {
	overflow: hidden;
}

/* Allow the editor area to shrink below content height so Monaco can scroll. */
.editor-code-area {
	min-height: 0;
}

.editor-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: var(--sgnl-space-4);
	padding: var(--sgnl-space-2) var(--sgnl-space-8);
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	flex-shrink: 0;
}

.editor-header__group {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-4);
}

.editor-header :deep(.v-btn--icon) {
	background: transparent;
}
</style>
